/**
 * EDD Form Route
 *
 * Enhanced Due Diligence form page for collecting user information.
 * Supports multiple trigger types (EDD application, UK risk profile, UK risk quiz, etc.)
 *
 * URL: /edd/CYM, /edd/CAN, /edd/HKG, /edd
 *
 * Uses React.lazy() for code-splitting to reduce initial bundle size.
 * Each form page is loaded only when needed.
 *
 * Special routes:
 * - /edd/HKG: Redirects to external HK EDD form based on environment
 */

import { lazy, Suspense, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FeatureControlProvider } from '@shared-web/contexts/FeatureControlContext';
import { composeProviders } from '@shared-web/utils/composeProviders';

// Lazy load form pages for code-splitting
// Use .then() to convert named exports to default exports for React.lazy()
const CANFormPage = lazy(() =>
  import('@shared-web/modules/edd/pages/can-form/index').then((module) => ({
    default: module.CANFormPage,
  })),
);
const CYMFormPage = lazy(() =>
  import('@shared-web/modules/edd/pages/cym-form/index').then((module) => ({
    default: module.CYMFormPage,
  })),
);
const EDDFormPage = lazy(() =>
  import('@shared-web/modules/edd/pages/EddFormPage').then((module) => ({
    default: module.EDDFormPage,
  })),
);

/**
 * Get external redirect URL for HKG EDD form based on environment
 */
function getHKGRedirectUrl(): string {
  const legacyWebUrl =
    import.meta.env.VITE_LEGACY_WEB_URL || 'https://crypto.com/exchange';

  return `${legacyWebUrl}/forms/hk/edd`;
}

/**
 * HKG Redirect Component
 * Redirects to external HK EDD form URL
 */
function HKGRedirect() {
  useEffect(() => {
    const url = getHKGRedirectUrl();
    window.location.href = url;
  }, []);

  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-muted-foreground">Redirecting to HK EDD form...</div>
    </div>
  );
}

// Loading component for Suspense fallback
function FormLoading() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-muted-foreground">Loading form...</div>
    </div>
  );
}

export default function EDDFormRoute() {
  const params = useParams();

  const EDDFormProviders = useMemo(
    () => composeProviders([[FeatureControlProvider]]),
    [],
  );

  // Extract country code from wildcard route parameter
  // Route: /edd/* -> params = { '*': 'CYM' }
  const countryCode = params['*']?.toUpperCase() || '';

  // Handle HKG redirect (case-insensitive)
  if (countryCode === 'HKG') {
    return <HKGRedirect />;
  }

  return (
    <EDDFormProviders>
      <Suspense fallback={<FormLoading />}>
        {countryCode === 'CYM' && <CYMFormPage />}
        {countryCode === 'CAN' && <CANFormPage />}
        {countryCode !== 'CYM' && countryCode !== 'CAN' && (
          <EDDFormPage countryCode={countryCode} />
        )}
      </Suspense>
    </EDDFormProviders>
  );
}
